import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaHome, FaInfoCircle, FaQuestionCircle, FaBalanceScale, FaShieldAlt, FaCookie, FaFileContract } from 'react-icons/fa';
import { HiMenu, HiX } from 'react-icons/hi';
import { useState, useEffect } from 'react';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home', icon: FaHome },
    { path: '/about', label: 'About Us', icon: FaInfoCircle },
    { path: '/comparison', label: 'Compare Offers', icon: FaBalanceScale },
    { path: '/faq', label: 'FAQ', icon: FaQuestionCircle },
  ];

  const footerLinks = [
    { path: '/privacy', label: 'Privacy Policy', icon: FaShieldAlt },
    { path: '/cookies', label: 'Cookie Policy', icon: FaCookie },
    { path: '/terms', label: 'Terms & Conditions', icon: FaFileContract },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-dark-900/95 backdrop-blur-xl shadow-xl' : 'bg-transparent'
      }`}>
        <nav className="container mx-auto px-4 py-4 bg-dark-900/95 backdrop-blur-xl shadow-xl">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <img 
                src={`${process.env.PUBLIC_URL}/images/logo.webp`}
                alt="JackpotQuantum Logo"
                className="h-12 w-auto transform group-hover:scale-110 transition-transform"
                loading="eager"
              />
              <span className="text-white font-semibold">JackpotQuantum</span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                      isActive
                        ? 'bg-quantum-500/20 text-quantum-400'
                        : 'text-gray-300 hover:text-quantum-400 hover:bg-dark-800'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{link.label}</span>
                  </Link>
                );
              })}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-gray-300 hover:text-quantum-400 transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <HiX className="w-6 h-6" /> : <HiMenu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-2">
              {navLinks.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-quantum-500/20 text-quantum-400'
                        : 'text-gray-300 hover:text-quantum-400 hover:bg-dark-800'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{link.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </nav>
      </header>

      <main className="flex-grow pt-20">
        {children}
      </main>

      <footer className="bg-dark-800 border-t border-dark-700 mt-auto">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src={`${process.env.PUBLIC_URL}/images/logo.webp`}
                  alt="JackpotQuantum Logo"
                  className="h-10 w-auto"
                  loading="lazy"
                />
              </div>
              <p className="text-gray-400 text-sm mb-4">
                Your trusted source for comprehensive UK casino reviews. We help you find the best offers, bonuses, and gaming experiences.
              </p>
              <div className="flex flex-wrap gap-4">
                <a 
                  href="https://www.gambleaware.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <img 
                    src={`${process.env.PUBLIC_URL}/images/footer/begambleaware.webp`} 
                    alt="BeGambleAware" 
                    className="h-8 opacity-70 hover:opacity-100 transition-opacity" 
                  />
                </a>
                <a 
                  href="https://www.gamblingcommission.gov.uk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <img 
                    src={`${process.env.PUBLIC_URL}/images/footer/gambling-commission.webp`} 
                    alt="Gambling Commission" 
                    className="h-8 opacity-70 hover:opacity-100 transition-opacity" 
                  />
                </a>
                <a 
                  href="https://www.gamcare.org.uk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <img 
                    src={`${process.env.PUBLIC_URL}/images/footer/gamcare.webp`} 
                    alt="GamCare" 
                    className="h-8 opacity-70 hover:opacity-100 transition-opacity" 
                  />
                </a>
                <a 
                  href="https://gamblingtherapy.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <img 
                    src={`${process.env.PUBLIC_URL}/images/footer/gambling-therapy-logo.webp`} 
                    alt="Gambling Therapy" 
                    className="h-8 opacity-70 hover:opacity-100 transition-opacity" 
                  />
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {navLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <li key={link.path}>
                      <Link
                        to={link.path}
                        className="flex items-center space-x-2 text-gray-400 hover:text-quantum-400 transition-colors text-sm"
                      >
                        <Icon className="w-3 h-3" />
                        <span>{link.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                {footerLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <li key={link.path}>
                      <Link
                        to={link.path}
                        className="flex items-center space-x-2 text-gray-400 hover:text-quantum-400 transition-colors text-sm"
                      >
                        <Icon className="w-3 h-3" />
                        <span>{link.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="border-t border-dark-700 mt-8 pt-8">
            {/* 18+ Disclaimer */}
            <div className="bg-dark-700/50 rounded-lg p-4 mb-6 text-center">
              <p className="text-white font-bold text-lg mb-2">
                <span className="text-quantum-400">18+</span> | Responsible Gambling
              </p>
              <p className="text-gray-400 text-sm mb-3">
                This website is intended for users who are 18 years of age or older. 
                Gambling can be addictive. Please play responsibly.
              </p>
              <div className="flex flex-wrap justify-center items-center gap-4 text-xs text-gray-500">
                <a 
                  href="https://www.gambleaware.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-quantum-400 transition-colors"
                >
                  BeGambleAware.org
                </a>
                <span>•</span>
                <a 
                  href="https://www.gamcare.org.uk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-quantum-400 transition-colors"
                >
                  GamCare.org.uk
                </a>
                <span>•</span>
                <a 
                  href="https://gamblingtherapy.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-quantum-400 transition-colors"
                >
                  GamblingTherapy.org
                </a>
              </div>
            </div>

            {/* Copyright */}
            <div className="text-center text-gray-500 text-sm">
              <p>&copy; {new Date().getFullYear()} JackpotQuantum.com. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;

